import { DEVICE_ID_HEADER } from "@shared/api";
import { isUuid, uuidv7 } from "@shared/ids";

/**
 * OWNED BY C1. The one fetch helper every API call uses, and the device identity every request carries.
 * The Better Auth client is in ./authClient and loads on demand (D-056).
 *
 * Every request — auth or API — carries X-Device-Id, the browser-install identity C4 generates in the
 * LocalStore and the Worker binds on first use. Credentials are the __Host-session cookie; no token is ever
 * stored in JS (S-010).
 */

/**
 * The device id must be on the very first /api/me call (the Worker answers 400 without it), which happens
 * before the LocalStore has opened. So the id is seeded from localStorage on module load (generated once per
 * browser install) and the LocalStore adopts the same seed on its first open, keeping one identity (D-031).
 */
export const DEVICE_ID_STORAGE_KEY = "wc.deviceId";

function seedDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existing && isUuid(existing)) return existing;
    const fresh = uuidv7();
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    return typeof localStorage === "undefined" ? uuidv7() : "";
  }
}

let deviceId = seedDeviceId();

/** Called by the shell once the LocalStore's meta is known; the store's id is authoritative. */
export function setDeviceId(id: string): void {
  if (!isUuid(id)) return;
  deviceId = id;
  try { localStorage.setItem(DEVICE_ID_STORAGE_KEY, id); } catch { /* private mode */ }
}
export function getDeviceId(): string {
  return deviceId;
}

export function withDeviceId(init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers);
  if (deviceId) headers.set(DEVICE_ID_HEADER, deviceId);
  return { ...init, headers, credentials: "include" };
}

export const authBaseUrl = (): string =>
  (typeof location !== "undefined" ? location.origin : "http://localhost") + "/auth";

/** Signs out through Better Auth, whose client is loaded only here and on the sign-in screen (D-056). */
export async function signOut(): Promise<void> {
  const { authClient } = await import("./authClient");
  await authClient.signOut();
}

/** fetch for /api routes: same credentials and device header, JSON in and out. */
export async function apiFetch<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const { json, ...rest } = init ?? {};
  const headers = new Headers(rest.headers);
  if (json !== undefined) headers.set("Content-Type", "application/json");
  const res = await fetch(path, {
    ...withDeviceId({ ...rest, headers }),
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      typeof body === "object" && body !== null && "error" in body &&
      typeof (body as { error: { message?: unknown } }).error?.message === "string"
        ? (body as { error: { message: string } }).error.message
        : `Request failed (${res.status})`;
    throw new ApiRequestError(message, res.status);
  }
  return body as T;
}

export class ApiRequestError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "ApiRequestError";
  }
}
